import api from '../API.js';

/**
 * Monitors a RAG job status using WebSocket notifications with polling fallback
 * Uses Girder's built-in job_status and progress notifications
 * @param {string} jobId - Job ID to monitor
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onProgress - Called with progress percentage (0-100)
 * @param {Function} callbacks.onSuccess - Called with {chunks} on success
 * @param {Function} callbacks.onError - Called with error message on failure
 * @param {Function} callbacks.onComplete - Called when monitoring completes (success or error)
 * @param {Object} options - Options object
 * @param {Object} options.girderUpdatesContext - GirderUpdatesContext from useGirderUpdates hook (optional)
 * @param {string} options.jobStatusEndpoint - Endpoint for job status (default: 'agent/rag/jobs')
 * @param {boolean} options.useWebSocket - Whether to use WebSocket (default: true, falls back to polling if unavailable)
 * @returns {Function} Cleanup function to stop monitoring
 */
export const monitorRagJobStatus = (
    jobId,
    { onProgress, onSuccess, onError, onComplete },
    options = {}
) => {
    const {
        girderUpdatesContext = null,
        jobStatusEndpoint = 'agent/rag/jobs',
        useWebSocket = true,
    } = options;

    let cleanupFunctions = [];
    let isComplete = false;

    const handleJobUpdate = (jobState) => {
        if (isComplete) {
            return;
        }

        console.log(`[RAG Job Monitor] handleJobUpdate called for job ${jobId}:`, {
            status: jobState.status,
            progress: jobState.progress,
            hasProgress: !!jobState.progress,
            progressTotal: jobState.progress?.total,
            progressCurrent: jobState.progress?.current,
            progressType: typeof jobState.progress?.total,
        });

        const { status, progress } = jobState;

        // Update progress if available (progress can come before status is set)
        if (progress && typeof progress.total === 'number' && progress.total > 0 && onProgress) {
            const current = typeof progress.current === 'number' ? progress.current : 0;
            const progressPercent = (current / progress.total) * 100;
            console.log(`[RAG Job Monitor] Progress update for job ${jobId}: ${current}/${progress.total} = ${progressPercent.toFixed(1)}%`);
            onProgress(progressPercent);
        } else if (progress && onProgress) {
            console.log(`[RAG Job Monitor] Progress data received but invalid:`, { 
                total: progress.total, 
                current: progress.current,
                totalType: typeof progress.total,
                currentType: typeof progress.current,
                progress 
            });
        } else if (!progress) {
            console.log(`[RAG Job Monitor] No progress data in jobState for job ${jobId}`);
        }

        // Check if complete
        if (status === 3) { // Success
            isComplete = true;
            
            // Read chunks from structured progress data
            let chunks = 0;
            
            // Primary: Read from job.progress.chunks (structured data)
            if (progress && typeof progress.chunks === 'number') {
                chunks = progress.chunks;
            }
            // Fallback: Extract from progress message (for backward compatibility)
            else if (progress && progress.message) {
                const progressMsg = String(progress.message);
                const progressMatch = progressMsg.match(/(\d+)\s+chunks?\s+(?:created|processed)/i) ||
                                    progressMsg.match(/chunks?\s*[:=]\s*(\d+)/i);
                if (progressMatch) {
                    chunks = parseInt(progressMatch[1], 10);
                }
            }
            
            if (chunks === 0) {
                console.warn('Job completed but chunks count is 0. Job state:', jobState);
            }
            
            if (onSuccess) {
                onSuccess({ chunks });
            }
            if (onComplete) {
                onComplete();
            }
        } else if (status === 4) { // Error
            isComplete = true;
            const errorMsg = 'Job failed'; // Could be enhanced with error details from jobState
            if (onError) {
                onError(errorMsg);
            }
            if (onComplete) {
                onComplete();
            }
        }
    };

    // Try WebSocket first if available
    if (useWebSocket && girderUpdatesContext && girderUpdatesContext.isConnected) {
        console.log(`[RAG Job Monitor] Using WebSocket for job ${jobId}`);
        
        // Subscribe to job updates via context
        // Note: jobType filter is optional - we'll accept any job type for this utility
        const unsubscribe = girderUpdatesContext.subscribeToJob(
            jobId, 
            handleJobUpdate
        );
        cleanupFunctions.push(unsubscribe);

        // Also get current state if available
        const currentState = girderUpdatesContext.getJobStatus(jobId);
        if (currentState) {
            handleJobUpdate(currentState);
        }
    } else {
        // Fallback to polling
        console.log(`[RAG Job Monitor] Using polling fallback for job ${jobId}`);
        return pollRagJobStatus(jobId, { onProgress, onSuccess, onError, onComplete }, jobStatusEndpoint);
    }

    // Return cleanup function
    return () => {
        isComplete = true;
        cleanupFunctions.forEach((cleanup) => {
            try {
                cleanup();
            } catch (error) {
                console.error('Error during cleanup:', error);
            }
        });
        cleanupFunctions = [];
    };
};

/**
 * Polls a RAG job status until completion or error (fallback method)
 * @param {string} jobId - Job ID to poll
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onProgress - Called with progress percentage (0-100)
 * @param {Function} callbacks.onSuccess - Called with {chunks} on success
 * @param {Function} callbacks.onError - Called with error message on failure
 * @param {Function} callbacks.onComplete - Called when polling completes (success or error)
 * @param {string} jobStatusEndpoint - Endpoint for job status (default: 'agent/rag/jobs')
 */
export const pollRagJobStatus = (
    jobId,
    { onProgress, onSuccess, onError, onComplete },
    jobStatusEndpoint = 'agent/rag/jobs'
) => {
    const poll = async () => {
        try {
            const statusResponse = await api.get(`${jobStatusEndpoint}/${jobId}`);
            const job = statusResponse.data;
            
            // Update progress if available
            if (job.progress && job.progress.total > 0 && onProgress) {
                const progressPercent = (job.progress.current / job.progress.total) * 100;
                onProgress(progressPercent);
            }
            
            // Check if complete
            if (job.status === 3) { // Success
                // Read chunks from structured progress data
                let chunks = 0;
                
                // Primary: Read from job.progress.chunks (structured data)
                if (job.progress && typeof job.progress.chunks === 'number') {
                    chunks = job.progress.chunks;
                }
                // Fallback: Extract from progress message (for backward compatibility)
                else if (job.progress && job.progress.message) {
                    const progressMsg = String(job.progress.message);
                    // Look for patterns like "Document processed: 42 chunks" or "42 chunks created"
                    const progressMatch = progressMsg.match(/(\d+)\s+chunks?\s+(?:created|processed)/i) ||
                                        progressMsg.match(/chunks?\s*[:=]\s*(\d+)/i);
                    if (progressMatch) {
                        chunks = parseInt(progressMatch[1], 10);
                    }
                }
                
                // Debug logging if chunks is still 0
                if (chunks === 0) {
                    console.warn('Job completed but chunks count is 0. Full job response:', {
                        status: job.status,
                        progress: job.progress,
                        progressChunks: job.progress?.chunks,
                        progressMessage: job.progress?.message
                    });
                }
                
                if (onSuccess) {
                    onSuccess({ chunks });
                }
                if (onComplete) {
                    onComplete();
                }
            } else if (job.status === 4) { // Error
                const errorMsg = job.log && job.log.length > 0 
                    ? job.log.join('\n') 
                    : 'Unknown error';
                if (onError) {
                    onError(errorMsg);
                }
                if (onComplete) {
                    onComplete();
                }
            } else if (job.status === 2 || job.status === 1) { // Running or Queued
                // Continue polling
                setTimeout(poll, 1000);
            } else {
                // Other status - continue polling
                setTimeout(poll, 1000);
            }
        } catch (err) {
            console.error('Error polling job status:', err);
            if (onError) {
                onError('Failed to check upload status');
            }
            if (onComplete) {
                onComplete();
            }
        }
    };
    
    // Start polling
    poll();
};
